import React, { useMemo, useRef, useState, useEffect } from 'react';
import { signInWithEmailAndPassword, signInWithPopup, signInWithRedirect, getRedirectResult, RecaptchaVerifier, signInWithPhoneNumber, createUserWithEmailAndPassword, updateProfile, sendEmailVerification, updatePassword, GoogleAuthProvider, signInWithCredential, applyActionCode, signOut } from 'firebase/auth';
import { auth, googleProvider, db } from '../firebase.config';
import { doc, getDoc, setDoc, serverTimestamp, collection, query, where, getDocs } from '../firebase.config';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import AuthService from '../utils/authService';
import { getAppDomain } from '../utils/domainConfig';
import { checkPhoneExists, normalizePhoneNumber, isPhone as isValidPhone } from '../utils/phoneValidation';
import UnifiedSessionManager from '../utils/unifiedSessionManager';
import ErrorHandler from '../utils/errorHandler';
import { normalizeEmail } from '../utils/emailUtils';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { Mail, Phone, Lock, Eye, EyeOff, ArrowLeft, CheckCircle2, XCircle, Smartphone, User, LogIn, Sparkles } from 'lucide-react';

export default function Login() {
  console.log('🚀 LOGIN COMPONENT RENDERED - Component is loading');
  const navigate = useNavigate();
  const { handleError: handleErrorNotification, handleValidationError, handleFirebaseError, showSuccess, showWarning, showInfo } = useErrorHandler();
  
  // Check for persistent session on component mount
  useEffect(() => {
    console.log('🚀 LOGIN COMPONENT MOUNTED - useEffect triggered');
    const checkPersistentSession = async () => {
      try {
        const sessionStatus = await UnifiedSessionManager.getSessionStatus();
        
        if (sessionStatus.isAuthenticated) {
          // Valid persistent session exists, redirect to dashboard
          console.log('🔄 Login: Valid persistent session found, redirecting to dashboard');
          navigate('/dashboard');
          return;
        } else if (sessionStatus.hasSession || sessionStatus.hasToken) {
          // Session exists but is expired, clear it
          console.log('❌ Login: Session expired, clearing storage');
          await UnifiedSessionManager.clearAllSessionData();
        }
        
        // Check for redirect result from Google OAuth
        console.log('🔄 Checking for Google OAuth redirect result...');
        const redirectResult = await getRedirectResult(auth);
        console.log('🔄 Redirect result:', redirectResult);
        
        if (redirectResult) {
          console.log('✅ Google OAuth redirect result found:', redirectResult);
          console.log('  - User:', redirectResult.user);
          console.log('  - Credential:', redirectResult.credential);
          console.log('  - Provider ID:', redirectResult.providerId);
          
          await ensureUserProfile(redirectResult.user);
          // Check if this is a first-time user (no companies)
          try {
            const { collection, query, where, getDocs } = await import('firebase/firestore');
            const companiesQuery = query(collection(db, 'companies'), where('owner.userId', '==', redirectResult.user.uid));
            const companiesSnap = await getDocs(companiesQuery);
            
            if (companiesSnap.empty) {
              // First-time user - show signup completion message
              setMessage('Welcome! Your account has been created. Please complete your company setup to continue.');
              setCurrentStep('signup');
              return;
            }
          } catch (error) {
            // Ignore error
          }
          
          // Save user session
          saveUserSession(redirectResult.user);
          clearLoginState();
          
          // Existing user - redirect to dashboard
          navigate('/dashboard');
        }
      } catch (error) {
        console.error('Login: Error checking persistent session:', error);
      }
    };
    
    checkPersistentSession();
  }, [navigate]);

  // Handle email verification link
  // NOTE: App.js also handles this globally. This handler is for login page specifically.
  // SECURITY: Added validation and duplicate prevention
  useEffect(() => {
    // Flag to prevent duplicate processing
    let isProcessing = false;
    
    const handleEmailVerification = async () => {
      // Prevent duplicate processing (App.js may have already handled it)
      if (isProcessing) {
        console.log('⚠️ Email verification already in progress in Login.js, skipping...');
        return;
      }
      
      // Check if App.js already processed this (check for processed flag in sessionStorage)
      const alreadyProcessed = sessionStorage.getItem('email_verification_processed');
      if (alreadyProcessed === 'true') {
        console.log('⚠️ Email verification already processed by App.js, skipping Login.js handler...');
        return;
      }
      
      try {
        // Check for email verification query parameters in URL
        const urlParams = new URLSearchParams(window.location.search);
        const mode = urlParams.get('mode');
        const oobCode = urlParams.get('oobCode');
        const apiKey = urlParams.get('apiKey');
        
        // Also check hash-based routing (for React Router)
        const hash = window.location.hash;
        const hashParams = new URLSearchParams(hash.split('?')[1] || '');
        const hashMode = hashParams.get('mode') || mode;
        const hashOobCode = hashParams.get('oobCode') || oobCode;
        const hashApiKey = hashParams.get('apiKey') || apiKey;
        
        // Validate parameters
        if (!hashMode || !hashOobCode) return;
        if (hashMode !== 'verifyEmail') return;
        if (hashOobCode.length < 20 || hashOobCode.length > 500) {
          console.error('❌ Invalid oobCode length');
          return;
        }
        
        if (hashMode === 'verifyEmail' && hashOobCode) {
          isProcessing = true;
          sessionStorage.setItem('email_verification_processed', 'true');
          console.log('📧 Email verification link detected in Login.js, processing...');
          setLoading(true);
          setMessage('Verifying your email address...');
          
          // SECURITY: Store current user email before verification (if logged in)
          const currentUserBefore = auth.currentUser;
          const currentUserEmailBefore = currentUserBefore?.email;
          
          try {
            // Apply the action code to verify the email
            await applyActionCode(auth, hashOobCode);
            console.log('✅ Email verified successfully!');
            
            // SECURITY: Validate email ownership if user is logged in
            // Reload the current user to get updated emailVerified status
            if (auth.currentUser) {
              await auth.currentUser.reload();
              const verifiedEmail = auth.currentUser.email;
              
              // If user was logged in before verification, verify the email matches
              if (currentUserEmailBefore && verifiedEmail !== currentUserEmailBefore) {
                console.error('🚨 SECURITY ALERT: Email verification mismatch!', {
                  loggedInAs: currentUserEmailBefore,
                  verifiedEmail: verifiedEmail
                });
                // This is a security issue - log out the current user
                await signOut(auth);
                setMessage('❌ Security error: Verification link does not match your account. Please log in again.');
                handleErrorNotification(new Error('Verification mismatch'), {
                  title: 'Security Error',
                  context: 'Email Verification'
                });
                return;
              }
              
              console.log('✅ User reloaded, emailVerified:', auth.currentUser.emailVerified);
            }
            
            // Update user document in Firestore with retry logic
            // CRITICAL: Ensure users document exists with emailVerified for Firestore rules fallback
            if (auth.currentUser) {
              let retries = 3;
              while (retries > 0) {
                try {
                  const userRef = doc(db, 'users', auth.currentUser.uid);
                  const userSnap = await getDoc(userRef);
                  const existingData = userSnap.exists() ? userSnap.data() : {};
                  
                  // Create or update user document with all required fields
                  await setDoc(userRef, {
                    uid: auth.currentUser.uid,
                    email: auth.currentUser.email || existingData.email || '',
                    displayName: auth.currentUser.displayName || existingData.displayName || '',
                    phoneNumber: auth.currentUser.phoneNumber || existingData.phoneNumber || '',
                    emailVerified: true, // CRITICAL: Set to true for Firestore rules
                    updatedAt: serverTimestamp(),
                    createdAt: existingData.createdAt || serverTimestamp(),
                    ...existingData // Preserve any existing fields
                  }, { merge: true });
                  console.log('✅ User document created/updated with emailVerified status');
                  break; // Success, exit retry loop
                } catch (firestoreError) {
                  retries--;
                  if (retries === 0) {
                    console.error('⚠️ Could not update Firestore user document after retries:', firestoreError);
                    // Don't fail verification if Firestore update fails
                  } else {
                    console.warn(`⚠️ Firestore update failed, retrying... (${retries} attempts left)`);
                    await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1s before retry
                  }
                }
              }
            }
            
            // Show success message
            showSuccess('Email verified successfully! You can now log in.', { 
              title: 'Email Verified',
              duration: 5000 
            });
            setMessage('✅ Email verified successfully! You can now log in.');
            
            // Clear URL parameters
            window.history.replaceState({}, document.title, window.location.pathname + window.location.hash.split('?')[0]);
            
            // Clear the processed flag after a delay
            setTimeout(() => {
              sessionStorage.removeItem('email_verification_processed');
            }, 5000);
            
            // If user is logged in, redirect to dashboard
            if (auth.currentUser) {
              setTimeout(() => {
                navigate('/dashboard');
              }, 2000);
            }
          } catch (error) {
            console.error('❌ Error verifying email:', error);
            let errorMessage = 'Failed to verify email. The link may have expired or already been used.';
            
            if (error.code === 'auth/expired-action-code') {
              errorMessage = 'The verification link has expired. Please request a new verification email.';
            } else if (error.code === 'auth/invalid-action-code') {
              errorMessage = 'The verification link is invalid or has already been used.';
            } else if (error.code === 'auth/user-disabled') {
              errorMessage = 'This account has been disabled. Please contact support.';
            }
            
            handleErrorNotification(error, {
              title: 'Verification Failed',
              context: 'Email Verification'
            });
            setMessage(`❌ ${errorMessage}`);
          } finally {
            setLoading(false);
            isProcessing = false;
          }
        }
      } catch (error) {
        console.error('Error in email verification handler:', error);
        isProcessing = false;
      }
    };
    
    handleEmailVerification();
  }, [navigate, showSuccess, handleErrorNotification]);

  // Set up auth state listener with proper cleanup
  useEffect(() => {
    let authStateUnsubscribe = null;
    
    const setupAuthStateListener = () => {
      authStateUnsubscribe = auth.onAuthStateChanged((user) => {
        if (user) {
          console.log('🔥 Auth state changed - user signed in:', user.email);
          // User is signed in, check if they need to complete setup
          if (currentStep === 'entry' || currentStep === 'signin') {
            // User just signed in, redirect to dashboard
            navigate('/dashboard');
          }
        } else {
          console.log('🔥 Auth state changed - user signed out');
          // User is signed out, ensure we're on the right step
          if (currentStep !== 'entry') {
            setCurrentStep('entry');
            setMessage('');
          }
        }
      });
    };
    
    setupAuthStateListener();
    
    // Cleanup function
    return () => {
      if (authStateUnsubscribe) {
        console.log('🧹 Cleaning up auth state listener');
        authStateUnsubscribe();
      }
    };
  }, [navigate, currentStep]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      console.log('🧹 Login component unmounting, cleaning up...');
      // Clear any pending timeouts or intervals
      // Auth state listener cleanup is handled in the useEffect above
    };
  }, []);

  // Handle Google login redirect result (for Capacitor) - REMOVED DUPLICATE
  // This is now handled in the main useEffect above to prevent conflicts
  
  const [currentStep, setCurrentStep] = useState(() => {
    return localStorage.getItem('loginStep') || 'entry';
  }); // 'entry', 'auth', 'signup', 'otp'
  const [phoneNumber, setPhoneNumber] = useState(() => {
    return localStorage.getItem('loginPhoneNumber') || '';
  });
  const [otpCode, setOtpCode] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const [entryIdentifier, setEntryIdentifier] = useState(() => {
    return localStorage.getItem('loginEntryIdentifier') || '';
  }); // Phone or Email entered
  const [userExists, setUserExists] = useState(() => {
    const stored = localStorage.getItem('loginUserExists');
    return stored ? JSON.parse(stored) : null;
  }); // null, true, false
  const [wrongPasswordCount, setWrongPasswordCount] = useState(0);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showSigninPassword, setShowSigninPassword] = useState(false);
  const [userDetails, setUserDetails] = useState(() => {
    const stored = localStorage.getItem('loginUserDetails');
    return stored ? JSON.parse(stored) : { 
      name: '', 
      email: '', 
      recoveryPhone: '',
      password: ''
    };
  });
  const [signinData, setSigninData] = useState(() => {
    const stored = localStorage.getItem('loginSigninData');
    return stored ? JSON.parse(stored) : { identifier: '', password: '' };
  });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const recaptchaRef = useRef(null);
  const [showBiometric, setShowBiometric] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [cachedUser, setCachedUser] = useState(null);
  const [isNewDevice, setIsNewDevice] = useState(true);
  const [loginMethod, setLoginMethod] = useState('phone'); // 'phone', 'mpin', 'email'
  const [skipBiometric, setSkipBiometric] = useState(false);
  const [biometricPreference, setBiometricPreference] = useState(null); // null, 'enabled', 'disabled'
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    symbol: false
  });
  const [autoDetectSupported, setAutoDetectSupported] = useState(false);
  const [isDetectingPhone, setIsDetectingPhone] = useState(false);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('loginStep', currentStep);
  }, [currentStep]);

  useEffect(() => {
    localStorage.setItem('loginPhoneNumber', phoneNumber);
  }, [phoneNumber]);

  useEffect(() => {
    localStorage.setItem('loginEntryIdentifier', entryIdentifier);
  }, [entryIdentifier]);

  useEffect(() => {
    localStorage.setItem('loginUserExists', JSON.stringify(userExists));
  }, [userExists]);

  useEffect(() => {
    localStorage.setItem('loginUserDetails', JSON.stringify(userDetails));
  }, [userDetails]);

  useEffect(() => {
    localStorage.setItem('loginSigninData', JSON.stringify(signinData));
  }, [signinData]);

  // Clear localStorage on successful login
  const clearLoginState = () => {
    localStorage.removeItem('loginStep');
    localStorage.removeItem('loginPhoneNumber');
    localStorage.removeItem('loginEntryIdentifier');
    localStorage.removeItem('loginUserExists');
    localStorage.removeItem('loginUserDetails');
    localStorage.removeItem('loginSigninData');
  };

  // Handle page refresh - restore OTP step if needed
  useEffect(() => {
    const handlePageRefresh = () => {
      if (currentStep === 'otp' && phoneNumber) {
        // If we're on OTP step and have phone number, resend OTP
        console.log('🔄 Page refreshed on OTP step, resending OTP...');
        sendPhoneOTP();
      }
    };

    handlePageRefresh();
  }, [currentStep, phoneNumber]);

  // Prevent refresh on signup form
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (currentStep === 'signup' || currentStep === 'otp') {
        e.preventDefault();
        e.returnValue = 'Are you sure you want to leave? Your progress will be lost.';
        return 'Are you sure you want to leave? Your progress will be lost.';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentStep]);

  // Helper function to check if input looks like phone
  const isPhone = (input) => {
    if (!input) return false;
    console.log('isPhone input:', input);
    
    // If contains @ or .com/.in etc, it's definitely email
    if (input.includes('@') || input.includes('.com') || input.includes('.in') || input.includes('.org') || input.includes('.net')) {
      console.log('Detected as email (contains @ or domain)');
      return false;
    }
    
    // Clean the input - remove spaces and check if only digits and +
    const cleaned = input.replace(/\s+/g, '');
    const isPhoneNumber = /^\+?\d{10,}$/.test(cleaned);
    console.log('Cleaned input:', cleaned, 'Is phone:', isPhoneNumber);
    
    return isPhoneNumber;
  };


  // Check if user exists and proceed to appropriate step
  const handleProceed = async () => {
    if (!entryIdentifier.trim()) {
      setMessage('Please enter phone number or email');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      console.log('Checking user for:', entryIdentifier);
      const isPhoneNumber = isPhone(entryIdentifier);
      console.log('Is phone number:', isPhoneNumber);
      
      let exists = false;

      if (isPhoneNumber) {
        // Phone number → Check phone_index collection
        console.log('Checking phone in phone_index...');
        const normalizedPhone = normalizePhoneNumber(entryIdentifier);
        const phoneResult = await checkPhoneExists(normalizedPhone);
        exists = phoneResult.exists;
        setPhoneNumber(normalizedPhone);
        console.log('Phone exists:', exists);
        
        // If phone exists, check if user has email for password login
        if (exists) {
          try {
            const phoneDoc = await getDoc(doc(db, 'phone_index', normalizedPhone));
            if (phoneDoc.exists()) {
              const phoneData = phoneDoc.data();
              const hasEmail = phoneData.email && phoneData.email.trim() !== '';
              console.log('Phone user has email:', hasEmail);
              
              if (hasEmail) {
                // User has email, go to password auth
                setCurrentStep('auth');
                return;
              } else {
                // User has no email, go to OTP
                setCurrentStep('otp');
                return;
              }
            }
          } catch (error) {
            console.error('Error checking phone user email:', error);
            // Fallback to OTP if error
            setCurrentStep('otp');
            return;
          }
        }
      } else {
        // Email → Check users collection directly
        console.log('Checking email in users collection...');
        const email = entryIdentifier.trim().toLowerCase();
        console.log('Searching for email:', email);
        
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('email', '==', email));
        const querySnapshot = await getDocs(q);
        exists = !querySnapshot.empty;
        console.log('Email found in users:', exists);
        
        // If not found in users, try phone_index as fallback
        if (!exists) {
          console.log('Trying phone_index fallback...');
          try {
            const phoneIndexRef = collection(db, 'phone_index');
            const phoneQuery = query(phoneIndexRef, where('email', '==', email));
            const phoneSnapshot = await getDocs(phoneQuery);
            exists = !phoneSnapshot.empty;
            console.log('Email found in phone_index:', exists);
          } catch (phoneError) {
            console.log('Phone index email fallback failed:', phoneError);
          }
        }
      }

      console.log('Final exists result:', exists);
      setUserExists(exists);
      
      // For email users, check if they exist
      if (!isPhoneNumber && exists) {
        setCurrentStep('auth'); // Ask for password
      } else if (!isPhoneNumber && !exists) {
        setCurrentStep('signup'); // Go to signup with pre-filled data
      }
      // Phone users are handled above with early returns
    } catch (error) {
      console.error('Error checking user existence:', error);
      const formattedMessage = ErrorHandler.formatError(error);
      setMessage(formattedMessage);
      handleErrorNotification(error, { context: 'UserExistenceCheck' });
    } finally {
      setLoading(false);
    }
  };

  // Session Management Functions
  const saveUserSession = async (userData) => {
    try {
      const sessionData = {
        uid: userData.uid,
        email: userData.email,
        phoneNumber: userData.phoneNumber,
        name: userData.displayName || userData.name,
        loginTime: Date.now(),
        loginMethod: loginMethod
      };
      
      // Use unified session manager
      const expiryTime = Date.now() + (24 * 60 * 60 * 1000); // 24 hours
      await UnifiedSessionManager.setUserSession(sessionData, expiryTime);
      
      // Save device info separately for compatibility
      const deviceInfo = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        timestamp: Date.now(),
        userId: userData.uid
      };
      
      if (UnifiedSessionManager.isCapacitor) {
        const { Preferences } = await import('@capacitor/preferences');
        await Preferences.set({
          key: 'acctoo_device_info',
          value: JSON.stringify(deviceInfo)
        });
      } else {
        localStorage.setItem('acctoo_device_info', JSON.stringify(deviceInfo));
      }
      
      console.log('✅ User session saved successfully');
    } catch (error) {
      console.error('Error saving session:', error);
    }
  };

  const clearUserSession = async () => {
    try {
      await UnifiedSessionManager.clearAllSessionData();
      console.log('✅ User session cleared');
    } catch (error) {
      console.error('Error clearing session:', error);
    }
  };

  const extendSession = async () => {
    try {
      const session = await UnifiedSessionManager.getUserSession();
      if (session) {
        const newExpiryTime = Date.now() + (24 * 60 * 60 * 1000);
        await UnifiedSessionManager.setUserSession(session.user, newExpiryTime);
        console.log('✅ Session extended for 24 hours');
      }
    } catch (error) {
      console.error('Error extending session:', error);
    }
  };


  // Password validation function
  const validatePassword = (password) => {
    try {
      if (!password) {
        setPasswordRequirements({
          length: false,
          uppercase: false,
          lowercase: false,
          number: false,
          symbol: false
        });
        return false;
      }
      
      const requirements = {
        length: password.length >= 8,
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
        number: /\d/.test(password),
        symbol: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
      };
      
      setPasswordRequirements(requirements);
      return Object.values(requirements).every(Boolean);
    } catch (error) {
      console.error('Password validation error:', error);
      setPasswordRequirements({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        symbol: false
      });
      return false;
    }
  };

  // Parse Firebase password error to extract specific requirements
  const parsePasswordError = (errorMessage) => {
    const requirements = [];
    
    if (errorMessage.includes('Password must contain a lower case character')) {
      requirements.push('lowercase letter (a-z)');
    }
    if (errorMessage.includes('Password must contain an upper case character')) {
      requirements.push('uppercase letter (A-Z)');
    }
    if (errorMessage.includes('Password must contain a non-alphanumeric character')) {
      requirements.push('special character (!@#$%^&*)');
    }
    if (errorMessage.includes('Password must be at least')) {
      const match = errorMessage.match(/Password must be at least (\d+) characters/);
      if (match) {
        requirements.push(`at least ${match[1]} characters`);
      }
    }
    
    return requirements;
  };

  // Use centralized error handler for consistent error formatting
  const formatSignupError = (error) => {
    return ErrorHandler.formatAuthError(error);
  };


  // Session Management & Caching
  useEffect(() => {
    const checkExistingSession = () => {
      try {
        // Check for cached user session
        const cachedUserData = localStorage.getItem('acctoo_user');
        const sessionExpiry = localStorage.getItem('acctoo_session_expiry');
        const deviceInfo = localStorage.getItem('acctoo_device_info');
        
        if (cachedUserData && sessionExpiry) {
          const userData = JSON.parse(cachedUserData);
          const expiryTime = parseInt(sessionExpiry);
          const currentTime = Date.now();
          
          // Check if session is still valid (24 hours)
          if (currentTime < expiryTime) {
            console.log('✅ Valid session found, redirecting to dashboard');
            // Navigate to dashboard - let App.js handle the authentication
            navigate('/dashboard');
            return;
          } else {
            console.log('⚠️ Session expired, clearing cache');
            localStorage.removeItem('acctoo_user');
            localStorage.removeItem('acctoo_session_expiry');
          }
        }
        
        // Check device info
        if (deviceInfo) {
          const device = JSON.parse(deviceInfo);
          setIsNewDevice(false);
          console.log('✅ Known device detected');
        } else {
          setIsNewDevice(true);
          console.log('🆕 New device detected');
        }
      } catch (error) {
        console.error('Session check error:', error);
        setIsNewDevice(true);
      }
    };
    
    checkExistingSession();
  }, [navigate]);

  // Check for phone number auto detect support
  useEffect(() => {
    const checkAutoDetectSupport = () => {
      // Check for WebOTP API support
      if ('OTPCredential' in window) {
        setAutoDetectSupported(true);
        console.log('✅ Phone auto detect supported');
      } else {
        setAutoDetectSupported(false);
        console.log('⚠️ Phone auto detect not supported');
      }
    };
    checkAutoDetectSupport();
  }, []);

  // Check for cached user data (known device)
  useEffect(() => {
    const checkCachedUser = () => {
      if (window.getCachedUser) {
        const cached = window.getCachedUser();
        if (cached) {
          setCachedUser(cached);
          setIsNewDevice(false);
          setPhoneNumber(cached.phoneNumber || '');
          console.log('✅ Known device detected:', cached);
        } else {
          setIsNewDevice(true);
          console.log('🆕 New device detected');
        }
      }
    };
    
    checkCachedUser();
  }, []);

  // Auto detect phone number using WebOTP API
  const autoDetectPhoneNumber = async () => {
    if (!autoDetectSupported) {
      setMessage('Phone auto detect not supported on this device');
      return;
    }

    setIsDetectingPhone(true);
    setMessage('Detecting phone number...');

    try {
      // Request phone number using WebOTP API
      const otpCredential = await navigator.credentials.get({
        otp: { transport: ['sms'] }
      });

      if (otpCredential && otpCredential.code) {
        // Extract phone number from OTP credential
        const phoneNumber = otpCredential.phoneNumber || '';
        if (phoneNumber) {
          setPhoneNumber(phoneNumber);
          setMessage('Phone number detected: ' + phoneNumber);
          console.log('✅ Phone number auto detected:', phoneNumber);
        } else {
          setMessage('Phone number not found in OTP credential');
        }
      } else {
        setMessage('Could not detect phone number');
      }
    } catch (error) {
      console.error('Phone auto detect error:', error);
      if (error.name === 'NotAllowedError') {
        setMessage('Phone number access denied. Please enter manually.');
      } else if (error.name === 'NotSupportedError') {
        setMessage('Phone auto detect not supported on this device.');
      } else {
        setMessage('Failed to detect phone number: ' + error.message);
      }
    } finally {
      setIsDetectingPhone(false);
    }
  };

  // New Device: Send OTP to phone
  const sendPhoneOTP = async () => {
    setLoading(true);
    setMessage('');

    try {
      const normalized = phoneNumber.trim().startsWith('+') ? phoneNumber.trim() : `+91${phoneNumber.trim()}`;
      const verifier = ensureRecaptcha();
      const conf = await signInWithPhoneNumber(auth, normalized, verifier);
      setConfirmation(conf);
      setCurrentStep('otp');
      setMessage('OTP sent to your phone. Please enter the code.');
    } catch (error) {
      const formattedMessage = ErrorHandler.formatError(error);
      setMessage(formattedMessage);
      handleErrorNotification(error, { context: 'SendOTP' });
    } finally {
      setLoading(false);
    }
  };

  // Auto detect OTP using WebOTP API
  const autoDetectOTP = async () => {
    if (!autoDetectSupported) {
      setMessage('OTP auto detect not supported on this device');
      return;
    }

    setLoading(true);
    setMessage('Detecting OTP...');

    try {
      // Request OTP using WebOTP API
      const otpCredential = await navigator.credentials.get({
        otp: { transport: ['sms'] }
      });

      if (otpCredential && otpCredential.code) {
        setOtpCode(otpCredential.code);
        setMessage('OTP detected: ' + otpCredential.code);
        console.log('✅ OTP auto detected:', otpCredential.code);
        
        // Automatically verify OTP
        setTimeout(() => {
          verifyOTPAndContinue();
        }, 1000);
      } else {
        setMessage('Could not detect OTP');
      }
    } catch (error) {
      console.error('OTP auto detect error:', error);
      if (error.name === 'NotAllowedError') {
        setMessage('OTP access denied. Please enter manually.');
      } else if (error.name === 'NotSupportedError') {
        setMessage('OTP auto detect not supported on this device.');
      } else {
        setMessage('Failed to detect OTP: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP and move to details collection
  const verifyOTPAndContinue = async () => {
    setLoading(true);
    setMessage('');

    try {
      if (!confirmation) throw new Error('No OTP session');
      const result = await confirmation.confirm(otpCode);
      await ensureUserProfile(result.user);
      
      // Save phone number to phone_index for future lookups
      try {
        const normalized = phoneNumber.trim().startsWith('+') ? phoneNumber.trim() : `+91${phoneNumber.trim()}`;
        const emailToIndex = result.user.email || 'temp@acctoo.com'; // Use temp email if none
        
        if (normalized) {
          await setDoc(doc(db, 'phone_index', normalized), { 
            uid: result.user.uid, 
            email: emailToIndex, 
            updatedAt: serverTimestamp() 
          }, { merge: true });
          
          // Also save 10-digit version for convenience
          if (/^\+91\d{10}$/.test(normalized)) {
            const tenDigit = normalized.slice(3);
            await setDoc(doc(db, 'phone_index', tenDigit), { 
              uid: result.user.uid, 
              email: emailToIndex, 
              updatedAt: serverTimestamp() 
            }, { merge: true });
          }
          console.log('✅ Phone number saved to phone_index:', normalized, 'with email:', emailToIndex);
        }
      } catch (phoneIndexError) {
        console.error('❌ Could not save phone to index:', phoneIndexError);
        setMessage('Phone saved but index update failed: ' + phoneIndexError.message);
      }
      
      setCurrentStep('details');
      setMessage('Phone verified! Please provide your details.');
    } catch (error) {
      const formattedMessage = ErrorHandler.formatError(error);
      setMessage(formattedMessage);
      // Determine error category for better handling
      if (error.code?.startsWith('auth/')) {
        handleFirebaseError(error, { context: 'VerifyOTP', title: 'OTP Verification Failed' });
      } else if (error.code === 'VALIDATION_ERROR') {
        handleValidationError(error, { context: 'VerifyOTP', title: 'OTP Verification Failed' });
      } else {
        handleErrorNotification(error, { context: 'VerifyOTP', title: 'OTP Verification Failed' });
      }
    } finally {
      setLoading(false);
    }
  };

  // Complete signup with user details
  const completeSignup = async () => {
    setLoading(true);
    setMessage('');

    try {
      // Get current user from phone auth
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('No authenticated user');

      // Validate password before proceeding
      if (userDetails.password) {
        const isPasswordValid = validatePassword(userDetails.password);
        if (!isPasswordValid) {
          const passwordError = new Error('Password does not meet requirements. Please ensure your password includes: at least 8 characters, one uppercase letter, one lowercase letter, one number, and one special character.');
          passwordError.code = 'VALIDATION_ERROR';
          passwordError.fieldName = 'password';
          handleValidationError(passwordError, {
            title: 'Password Requirements',
            context: 'Signup'
          });
          setLoading(false);
          return;
        }
      }

      // Update profile with collected details
      if (userDetails.name) {
        await updateProfile(currentUser, { displayName: userDetails.name });
      }

      // Update Firebase Authentication password if provided
      if (userDetails.password && userDetails.password.length >= 8) {
        try {
          await updatePassword(currentUser, userDetails.password);
          console.log('✅ Password updated in Firebase Authentication');
        } catch (passwordError) {
          console.error('Failed to update Firebase Auth password:', passwordError);
          // Handle password update errors with notifications
          if (passwordError.code === 'auth/weak-password' || passwordError.message?.includes('password-does-not-meet-requirements')) {
            const error = new Error(ErrorHandler.formatPasswordRequirementsError(passwordError.message));
            error.code = 'VALIDATION_ERROR';
            error.fieldName = 'password';
            handleValidationError(error, {
              title: 'Password Requirements',
              context: 'Signup'
            });
          } else {
            handleFirebaseError(passwordError, {
              title: 'Password Update Failed',
              context: 'Signup'
            });
          }
          setLoading(false);
          return;
        }
      }

      // Create comprehensive user profile with all details
      await AuthService.createUserProfile(currentUser, null, {
        displayName: userDetails.name || '',
        phoneNumber: phoneNumber,
        email: userDetails.email || currentUser.email || '',
        recoveryPhones: userDetails.recoveryPhone ? [userDetails.recoveryPhone] : [],
        password: userDetails.password || '' // Store password for future login
      });

      // Password and MPIN are already saved in AuthService.createUserProfile
      console.log('✅ Password and MPIN saved to users collection via AuthService');

      // Update phone_index with final email (if provided)
      try {
        const normalized = phoneNumber.trim().startsWith('+') ? phoneNumber.trim() : `+91${phoneNumber.trim()}`;
        const finalEmail = userDetails.email || currentUser.email || '';
        if (normalized && finalEmail) {
          await setDoc(doc(db, 'phone_index', normalized), { 
            uid: currentUser.uid, 
            email: finalEmail, 
            updatedAt: serverTimestamp() 
          }, { merge: true });
          
          // Also save 10-digit version for convenience
          if (/^\+91\d{10}$/.test(normalized)) {
            const tenDigit = normalized.slice(3);
            await setDoc(doc(db, 'phone_index', tenDigit), { 
              uid: currentUser.uid, 
              email: finalEmail, 
              updatedAt: serverTimestamp() 
            }, { merge: true });
          }
          console.log('✅ Phone number updated in phone_index with final email:', normalized);
        }
      } catch (phoneIndexError) {
        console.log('⚠️ Could not update phone_index:', phoneIndexError);
        // Don't fail the signup for this
      }

      // If email provided, send verification
      if (userDetails.email) {
        try {
          const actionCodeSettings = {
            url: `${getAppDomain()}/#/login`,
            handleCodeInApp: false,
          };
          await sendEmailVerification(currentUser, actionCodeSettings);
          showSuccess('Account created! Check your email for verification.', { title: 'Account Created' });
        } catch (emailError) {
          console.log('Email verification failed:', emailError);
          // Non-critical error, don't block signup
        }
      } else {
        showSuccess('Account created successfully!', { title: 'Account Created' });
      }

      // Check for existing companies
      try {
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        const companiesQuery = query(collection(db, 'companies'), where('owner.userId', '==', currentUser.uid));
        const companiesSnap = await getDocs(companiesQuery);

        if (companiesSnap.empty) {
          showInfo('Welcome! Please complete your company setup.', { title: 'Welcome' });
          return;
        }
      } catch (error) {
        console.log('Could not check user companies:', error);
      }

      // Save user session
      saveUserSession(currentUser);
      clearLoginState();
      
      navigate('/dashboard');
    } catch (error) {
      console.error('Signup error:', error);
      // Determine error category and show appropriate notification
      if (error.code?.startsWith('auth/')) {
        // Handle Firebase Auth errors (including password errors)
        if (error.code === 'auth/weak-password' || error.message?.includes('password-does-not-meet-requirements')) {
          const passwordError = new Error(ErrorHandler.formatPasswordRequirementsError(error.message));
          passwordError.code = 'VALIDATION_ERROR';
          passwordError.fieldName = 'password';
          handleValidationError(passwordError, {
            title: 'Password Requirements',
            context: 'Signup'
          });
        } else {
          handleFirebaseError(error, {
            title: 'Signup Failed',
            context: 'Signup'
          });
        }
      } else if (error.code === 'VALIDATION_ERROR' || error.fieldName) {
        handleValidationError(error, {
          title: 'Signup Failed',
          context: 'Signup'
        });
      } else {
        handleErrorNotification(error, {
          title: 'Signup Failed',
          context: 'Signup'
        });
      }
      // Also set message for inline display if needed
      setMessage('Signup failed: ' + formatSignupError(error));
    } finally {
      setLoading(false);
    }
  };

  // Quick signin for known devices
  const quickSignin = async () => {
    setLoading(true);
    setMessage('');
    
    try {
      if (!cachedUser) {
        const error = new Error('No cached user found. Please use full signin.');
        error.code = 'VALIDATION_ERROR';
        handleValidationError(error, {
          title: 'Quick Signin',
          context: 'Authentication'
        });
        setMessage('No cached user found. Please use full signin.');
        return;
      }
      
      // Auto-login with cached user data
        const userData = {
          uid: cachedUser.userId,
          email: cachedUser.email,
          displayName: cachedUser.displayName
        };
        
        localStorage.setItem('acctoo_user', JSON.stringify(userData));
      setMessage('Welcome back!');
        
      // Check for companies
        try {
          const { collection, query, where, getDocs } = await import('firebase/firestore');
          const companiesQuery = query(collection(db, 'companies'), where('owner.userId', '==', cachedUser.userId));
          const companiesSnap = await getDocs(companiesQuery);
          
          if (companiesSnap.empty) {
            setMessage('Welcome! Please complete your company setup.');
            return;
          }
        } catch (error) {
          console.log('Could not check user companies:', error);
        }
        
        // Save user session
        saveUserSession(cachedUser);
        clearLoginState();
        
        navigate('/dashboard');
    } catch (error) {
      handleErrorNotification(error, {
        title: 'Quick Signin Failed',
        context: 'Authentication'
      });
      setMessage('Quick signin failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Alternative sign-in for existing users (used in signin step)
  const doAlternativeSignin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    try {
      let loginEmail = signinData.identifier;
      let errorMessage = '';

      // Check if identifier looks like a phone number
      const looksLikePhone = isPhone(signinData.identifier);
      
      // If user typed a phone number, resolve to their email to avoid SMS OTP costs
      if (looksLikePhone) {
        const trimmed = signinData.identifier.trim();
        const normalized = trimmed.startsWith('+') ? trimmed : `+91${trimmed}`;
        try {
          // Use public phone index so read does not require auth
          let emailFromIndex = '';
          const idxDoc = await getDoc(doc(db, 'phone_index', normalized));
          if (idxDoc.exists()) {
            emailFromIndex = idxDoc.data()?.email || '';
          }
          // Legacy: try unprefixed key
          if (!emailFromIndex && /^\d{10}$/.test(trimmed)) {
            const altIdx = await getDoc(doc(db, 'phone_index', trimmed));
            if (altIdx.exists()) {
              emailFromIndex = altIdx.data()?.email || '';
            }
          }
          if (emailFromIndex) {
            loginEmail = emailFromIndex;
            console.log('📱 Phone resolved via index to email:', loginEmail);
          } else {
            errorMessage = 'Phone number not found in our system';
          }
        } catch (err) {
          console.error('Phone lookup error:', err);
          errorMessage = 'Error looking up phone number';
        }
        
        if (errorMessage) {
          setMessage(errorMessage);
          setLoading(false);
          return;
        }
      }
      
      // Validate that we have a proper email for login
      if (!loginEmail || !loginEmail.includes('@')) {
        setMessage('Please enter a valid email address or phone number');
        setLoading(false);
        return;
      }

      // If loginEmail was derived from phone, show a friendly prompt
      if (looksLikePhone && loginEmail) {
        try {
          const masked = (() => {
            const [name, domain] = loginEmail.split('@');
            if (!name) return loginEmail;
            const head = name.slice(0, Math.min(3, name.length));
            return `${head}${'*'.repeat(Math.max(0, name.length - head.length))}@${domain}`;
          })();
          setMessage(`Welcome! Continue as ${masked}`);
        } catch (_) {}
      }
      
      const res = await AuthService.login(loginEmail, signinData.password, null);
      if (!res.success) throw new Error(res.error || 'Login failed');
      // Hard guarantee: ensure Firestore user profile exists even if migration path skipped
      try { await ensureUserProfile(res.user); } catch (_) {}
      
      // Check if this is a first-time user (no companies)
      // If so, don't redirect to dashboard - let the FirstTimeCompanyWizard handle it
      try {
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        const companiesQuery = query(collection(db, 'companies'), where('owner.userId', '==', res.user.uid));
        const companiesSnap = await getDocs(companiesQuery);
        
        if (companiesSnap.empty) {
          // First-time user - don't redirect, let wizard handle it
          setMessage('Welcome! Please complete your company setup.');
          return;
        }
      } catch (error) {
        console.log('Could not check user companies:', error);
      }
      
      // Save user session
      saveUserSession(auth.currentUser);
      clearLoginState();
      
      // Existing user - redirect to dashboard
      navigate('/dashboard');
    } catch (e) {
      // Determine error category
      if (e.code?.startsWith('auth/')) {
        handleFirebaseError(e, {
          title: 'Login Failed',
          context: 'Authentication'
        });
      } else if (e.code === 'VALIDATION_ERROR') {
        handleValidationError(e, {
          title: 'Login Failed',
          context: 'Authentication'
        });
      } else {
        handleErrorNotification(e, {
          title: 'Login Failed',
          context: 'Authentication'
        });
      }
      setMessage(e?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // Simple test function to verify button clicks work
  const testButtonClick = () => {
    console.log('🔴 TEST BUTTON CLICKED - This should appear in logcat');
    showInfo('Test button clicked - Check logcat for logs', { title: 'Test' });
  };

  const doGoogle = async () => {
    console.log('🚀 DOGOOGLE FUNCTION CALLED - STARTING OAUTH PROCESS');
    setLoading(true);
    setMessage('');
    
    try {
      // Clear any existing session data to prevent conflicts
      localStorage.removeItem('acctoo_persistent_refreshed');
      
      // Platform detection using Capacitor API
      const isNativePlatform = Capacitor.isNativePlatform();
      
      console.log('🔍 OAUTH PLATFORM DETECTION:');
      console.log('  - isNativePlatform:', isNativePlatform);
      console.log('  - Platform:', Capacitor.getPlatform());
      console.log('  - userAgent:', navigator.userAgent);
      
      let result;
      
      if (isNativePlatform) {
        // ==========================================
        // NATIVE PLATFORM (Android/iOS) - Use Capacitor Firebase Plugin
        // ==========================================
        console.log('📱 NATIVE OAUTH FLOW - Using @capacitor-firebase/authentication');
        console.log('  - Platform:', Capacitor.getPlatform());
        console.log('  - Flow: Native Google OAuth → signInWithCredential');
        console.log('  - Package:', navigator.userAgent);
        
        try {
          setMessage('Opening native Google Sign-In...');
          
          // Step 1: Import and verify the Capacitor Firebase Authentication plugin
          console.log('🔧 Step 1: Importing Capacitor Firebase Authentication plugin...');
          const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
          console.log('✅ Plugin imported successfully');
          console.log('  - Plugin object:', FirebaseAuthentication);
          console.log('  - Available methods:', Object.keys(FirebaseAuthentication));
          
          // Step 2: Check if plugin is available and configured
          console.log('🔧 Step 2: Checking plugin availability...');
          if (!FirebaseAuthentication || !FirebaseAuthentication.signInWithGoogle) {
            console.error('❌ FirebaseAuthentication plugin not available or signInWithGoogle method missing');
            throw new Error('Native Google Sign-In plugin is not properly configured. Please use alternative login method.');
          }
          console.log('✅ Plugin is available and signInWithGoogle method exists');
          
          // Step 3: Call native Google Sign-In
          console.log('🔧 Step 3: Calling FirebaseAuthentication.signInWithGoogle()...');
          console.log('  - This will open the native Google account picker');
          console.log('  - User should see native Android/iOS UI');
          
          const nativeResult = await FirebaseAuthentication.signInWithGoogle();
          
          console.log('✅ Native Google Sign-In returned!');
          console.log('  - Full result:', JSON.stringify(nativeResult, null, 2));
          console.log('  - User object:', nativeResult.user);
          console.log('  - Credential object:', nativeResult.credential);
          console.log('  - User email:', nativeResult.user?.email);
          console.log('  - User ID:', nativeResult.user?.uid);
          console.log('  - ID Token exists:', !!nativeResult.credential?.idToken);
          console.log('  - Access Token exists:', !!nativeResult.credential?.accessToken);
          
          // Step 4: Validate ID token
          if (!nativeResult.credential?.idToken) {
            console.error('❌ No ID token received from native Google Sign-In');
            console.error('  - Credential object:', nativeResult.credential);
            throw new Error('No ID token received from native Google Sign-In. Authentication incomplete.');
          }
          console.log('✅ ID Token received successfully');
          console.log('  - Token length:', nativeResult.credential.idToken.length);
          
          // Step 5: Create Firebase credential
          console.log('🔧 Step 4: Creating Firebase credential from ID token...');
          const credential = GoogleAuthProvider.credential(nativeResult.credential.idToken);
          console.log('✅ Firebase credential created');
          console.log('  - Credential object:', credential);
          
          // Step 6: Sign in to Firebase with credential
          console.log('🔧 Step 5: Signing in to Firebase with credential...');
          result = await signInWithCredential(auth, credential);
          
          console.log('✅ Firebase sign-in with credential SUCCESSFUL!');
          console.log('  - Firebase User Email:', result.user.email);
          console.log('  - Firebase User ID:', result.user.uid);
          console.log('  - Provider:', result.providerId);
          console.log('  - Display Name:', result.user.displayName);
          console.log('  - Photo URL:', result.user.photoURL);
          
        } catch (error) {
          console.error('❌ NATIVE OAUTH FAILED - DETAILED ERROR:');
          console.error('  - Error object:', error);
          console.error('  - Error code:', error.code);
          console.error('  - Error message:', error.message);
          console.error('  - Error stack:', error.stack);
          console.error('  - Error name:', error.name);
          
          // Check for specific error types
          if (error.message?.includes('Plugin') || error.message?.includes('not available')) {
            console.error('🔴 PLUGIN NOT AVAILABLE ERROR');
            console.error('  - This means @capacitor-firebase/authentication is not installed or configured');
            console.error('  - Falling back to web OAuth flow');
            
            // Try fallback to web flow
            try {
              console.log('🔄 Attempting fallback to web OAuth...');
              setMessage('Native sign-in unavailable. Trying web fallback...');
              result = await signInWithPopup(auth, googleProvider);
              console.log('✅ Fallback web OAuth successful');
            } catch (fallbackError) {
              console.error('❌ Fallback also failed:', fallbackError);
              throw new Error('Google Sign-In failed on both native and web. Please use phone or email login.');
            }
          } else {
            // Provide user-friendly error messages
            let userMessage = '';
            
            if (error.message?.includes('canceled') || error.message?.includes('cancelled') || error.code === '12501') {
              userMessage = 'Google sign-in was cancelled. Please try again.';
            } else if (error.code === 'auth/network-request-failed') {
              userMessage = 'Network error. Please check your internet connection and try again.';
            } else if (error.code === 'auth/invalid-credential') {
              userMessage = 'Invalid Google credentials. Please try again or use phone/email login.';
            } else if (error.code === '10' || error.message?.includes('DEVELOPER_ERROR')) {
              userMessage = 'OAuth configuration error (SHA fingerprint mismatch). Please use phone/email login.';
            } else {
              userMessage = `Native Google sign-in failed: ${error.message}. Please use phone or email login.`;
            }
            
            throw new Error(userMessage);
          }
        }
        
      } else {
        // ==========================================
        // WEB PLATFORM (Browser) - Use Firebase Web SDK
        // ==========================================
        console.log('🌐 WEB OAUTH FLOW - Using Firebase signInWithPopup');
        console.log('  - Platform: Web Browser');
        console.log('  - OAuth Client: Web client (type 3)');
        console.log('  - Flow: Popup (opens in new window)');
        
        try {
          setMessage('Opening Google Sign-In popup...');
          
          // Use Firebase Web SDK popup flow
          result = await signInWithPopup(auth, googleProvider);
          
          console.log('✅ Web OAuth popup successful');
          console.log('  - User:', result.user.email);
          console.log('  - Provider:', result.providerId);
          
        } catch (error) {
          console.error('❌ WEB OAUTH FAILED:');
          console.error('  - Error code:', error.code);
          console.error('  - Error message:', error.message);
          
          // Provide user-friendly error messages
          let userMessage = '';
          
          if (error.code === 'auth/popup-closed-by-user') {
            userMessage = 'Google sign-in was cancelled. Please try again.';
          } else if (error.code === 'auth/popup-blocked') {
            userMessage = 'Popup was blocked by your browser. Please allow popups and try again.';
          } else if (error.code === 'auth/network-request-failed') {
            userMessage = 'Network error. Please check your internet connection and try again.';
          } else if (error.code === 'auth/unauthorized-domain') {
            userMessage = 'This domain is not authorized for Google sign-in. Please contact support.';
          } else {
            userMessage = `Google sign-in failed: ${error.message}`;
          }
          
          throw new Error(userMessage);
        }
      }
      
      // Only process result for web popup flow (mobile redirect is handled elsewhere)
      if (result) {
        await ensureUserProfile(result.user);
      }
      
      // If user provided phone number, save it to phone_index (only for web popup flow)
      if (result && phoneNumber) {
        try {
          const normalized = phoneNumber.trim().startsWith('+') ? phoneNumber.trim() : `+91${phoneNumber.trim()}`;
          const emailToIndex = auth.currentUser.email || '';
          if (normalized && emailToIndex) {
            await setDoc(doc(db, 'phone_index', normalized), { 
              uid: auth.currentUser.uid, 
              email: emailToIndex, 
              updatedAt: serverTimestamp() 
            }, { merge: true });
            
            // Also save 10-digit version for convenience
            if (/^\+91\d{10}$/.test(normalized)) {
              const tenDigit = normalized.slice(3);
              await setDoc(doc(db, 'phone_index', tenDigit), { 
                uid: auth.currentUser.uid, 
                email: emailToIndex, 
                updatedAt: serverTimestamp() 
              }, { merge: true });
            }
          }
        } catch (phoneIndexError) {
          // Don't fail the login for this
        }
      }
      
      // Only process post-login logic for web popup flow (mobile redirect is handled elsewhere)
      if (result) {
        // Check if this is a first-time user (no companies)
        try {
          const { collection, query, where, getDocs } = await import('firebase/firestore');
          const companiesQuery = query(collection(db, 'companies'), where('owner.userId', '==', auth.currentUser.uid));
          const companiesSnap = await getDocs(companiesQuery);
          
          if (companiesSnap.empty) {
            // First-time user - show signup completion message
            setMessage('Welcome! Your account has been created. Please complete your company setup to continue.');
            setCurrentStep('signup');
            return;
          }
        } catch (error) {
          // Ignore error
        }
        
        // Save user session
        saveUserSession(auth.currentUser);
        clearLoginState();
        
        // Existing user - redirect to dashboard
        navigate('/dashboard');
      }
    } catch (e) {
      console.error('❌ Google sign-in error:', e);
      setMessage(e?.message || 'Google sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const ensureRecaptcha = () => {
    if (recaptchaRef.current) return recaptchaRef.current;
    recaptchaRef.current = new RecaptchaVerifier(auth, 'login-recaptcha-container', { size: 'invisible' });
    return recaptchaRef.current;
  };

  // This function is now handled by sendPhoneOTP above - keeping for compatibility
  const doPhoneStart = async (e) => {
    if (e) e.preventDefault();
    return sendPhoneOTP();
  };

  const doPhoneVerify = async () => {
    setLoading(true);
    setMessage('');
    try {
      if (!confirmation) throw new Error('No OTP session');
      const result = await confirmation.confirm(otpCode);
      await ensureUserProfile(result.user);

      // Check if this is a first-time user (no companies)
      try {
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        const companiesQuery = query(collection(db, 'companies'), where('owner.userId', '==', result.user.uid));
        const companiesSnap = await getDocs(companiesQuery);
        
        if (companiesSnap.empty) {
          // First-time user - don't redirect, let wizard handle it
          setMessage('Welcome! Please complete your company setup.');
          return;
        }
      } catch (error) {
        console.log('Could not check user companies:', error);
      }
      
      // Save user session
      saveUserSession(auth.currentUser);
      clearLoginState();
      
      // Existing user - redirect to dashboard
      navigate('/dashboard');
    } catch (e) {
      setMessage(e?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  // Legacy signup function - now handled by completeSignup above
  const doSignup = async (e) => {
    if (e) e.preventDefault();
    // For legacy compatibility, create a minimal signup object
    const legacySignup = {
      name: userDetails.name,
      email: userDetails.email,
      phone: phoneNumber,
      password: '', // Not used in new flow
      recoveryPhones: userDetails.recoveryPhone
    };

    setLoading(true);
    setMessage('');
    try {
      // Validate phone uniqueness before creating account
      if (legacySignup.phone && isValidPhone(legacySignup.phone)) {
        const phoneCheck = await checkPhoneExists(normalizePhoneNumber(legacySignup.phone));
        if (phoneCheck.exists) {
          setMessage(`❌ ${phoneCheck.message}`);
          setLoading(false);
          return;
        }
      }
      
      // For new flow, we don't create password accounts directly
      // Instead, use the phone auth flow
      setMessage('Please use the phone number flow for new accounts');
      setCurrentStep('phone');
    } catch (e) {
      setMessage('Signup failed: ' + formatSignupError(e));
    } finally {
      setLoading(false);
    }
  };

  const ensureUserProfile = async (user) => {
    try {
      if (!user) return;
      const profile = await AuthService.getUserProfile(user.uid);
      if (!profile?.success) {
        await AuthService.createUserProfile(user, null, {
          displayName: user.displayName || '',
          phoneNumber: user.phoneNumber || '',
          email: user.email || '',
        });
      }
      // After login, ensure phone_index exists for existing users (primary phone only)
      try {
        const p = profile?.data?.phoneNumber || '';
        const primary = (p || '').trim();
        const e164 = primary ? (primary.startsWith('+') ? primary : `+91${primary}`) : '';
        const emailToIndex = user.email || '';
        if (e164 && emailToIndex) {
          await setDoc(doc(db, 'phone_index', e164), { uid: user.uid, email: emailToIndex, updatedAt: serverTimestamp() }, { merge: true });
          // Also write legacy 10-digit for convenience
          if (/^\+91\d{10}$/.test(e164)) {
            const ten = e164.slice(3);
            await setDoc(doc(db, 'phone_index', ten), { uid: user.uid, email: emailToIndex, updatedAt: serverTimestamp() }, { merge: true });
          }
        }
      } catch (_) {}
      // Removed legacy artifacts/companyDetails seeding and mirrors
    } catch (_) {}
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100">
        {/* Gradient Header */}
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-6 text-white">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="p-2 bg-white bg-opacity-20 rounded-xl backdrop-blur-sm">
              <Sparkles className="w-6 h-6" />
            </div>
            <h1 className="text-3xl font-bold">ACCTOO</h1>
          </div>
          <p className="text-center text-blue-100 text-sm">
            {isNewDevice ? 'New device? Let\'s get you set up!' : 'Welcome back!'}
          </p>
        </div>

        <div className="p-6">

        {/* Page 1: Entry (Phone/Email) */}
        {currentStep === 'entry' && (
          <div className="space-y-4">
            {/* Known Device Quick Login */}
            {!isNewDevice && cachedUser && (
              <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl mb-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-full flex items-center justify-center text-lg font-bold shadow-lg">
                      {cachedUser.displayName?.charAt(0) || 'U'}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-800">{cachedUser.displayName || 'User'}</div>
                      <div className="text-xs text-gray-600 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3 text-green-600" />
                        Known device
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={quickSignin}
                    disabled={loading}
                    className="px-5 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl text-sm font-medium hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
                  >
                    {loading ? 'Signing in...' : 'Quick Login'}
                  </button>
                </div>
              </div>
            )}

            {/* Phone/Email Input */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <Mail className="w-4 h-4 text-blue-600" />
                Enter your phone number or email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  {isPhone(entryIdentifier) ? (
                    <Phone className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Mail className="h-5 w-5 text-gray-400" />
                  )}
                </div>
                <input
                  type="text"
                  value={entryIdentifier}
                  onChange={(e) => setEntryIdentifier(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !loading && entryIdentifier.trim()) {
                      handleProceed();
                    }
                  }}
                  placeholder="Phone number or email"
                  className="w-full pl-12 pr-4 py-3.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base transition-all duration-200 bg-gray-50 focus:bg-white"
                  required
                />
              </div>
            </div>

            <button
              onClick={() => {
                console.log('Proceed button clicked with:', entryIdentifier);
                handleProceed();
              }}
              disabled={loading || !entryIdentifier.trim()}
              className="w-full bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-3.5 px-4 rounded-xl hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-[1.02] flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Checking...</span>
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  <span>Continue</span>
                </>
              )}
            </button>

            {/* Biometric Login (if enabled) */}
            {showBiometric && (
              <button
                onClick={() => {/* TODO: Implement biometric login */}}
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-3 border border-gray-300 px-4 py-3 rounded-lg bg-white text-gray-800 hover:bg-gray-50 text-lg"
              >
                <span className="text-2xl">🔐</span>
                Use Biometric
              </button>
            )}

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500 font-medium">Or continue with</span>
              </div>
            </div>

            {/* TEST BUTTON - Hidden in production */}
            {process.env.NODE_ENV === 'development' && (
              <button
                onClick={testButtonClick}
                className="w-full inline-flex items-center justify-center gap-2 border-2 border-red-300 px-4 py-2.5 rounded-xl bg-red-50 text-red-700 hover:bg-red-100 text-sm mb-4 font-medium transition-all"
              >
                🔴 TEST BUTTON
              </button>
            )}

            <button
              onClick={doGoogle}
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-3 border-2 border-gray-200 px-4 py-3.5 rounded-xl bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300 text-base font-medium shadow-sm hover:shadow-md transition-all duration-200 hover:scale-[1.02]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 533.5 544.3">
                <path fill="#4285F4" d="M533.5 278.4c0-17.4-1.6-34.1-4.6-50.2H272v95h147.3c-6.4 34.7-25.7 64.1-54.8 83.9v69.7h88.7c51.9-47.8 80.3-118.3 80.3-198.4z"/>
                <path fill="#34A853" d="M272 544.3c73.8 0 135.8-24.4 181.1-66.5l-88.7-69.7c-24.6 16.5-56.2 26-92.4 26-71 0-131.1-47.9-152.6-112.3H28v70.5C73.9 486.6 165.8 544.3 272 544.3z"/>
                <path fill="#FBBC05" d="M119.4 321.8c-10.4-30.8-10.4-64 0-94.8V156.5H28c-41.7 83.4-41.7 183 0 266.4l91.4-70.6z"/>
                <path fill="#EA4335" d="M272 107.7c39.9 0 75.7 13.7 103.9 40.6l77.9-77.9C407.8 24.9 345.8 0 272 0 165.8 0 73.9 57.7 28 156.5l91.4 70.5C140.9 155.1 201 107.7 272 107.7z"/>
              </svg>
              Continue with Google
            </button>
              </div>
            )}

        {/* Page 2: Authentication (Password/MPIN) */}
        {currentStep === 'auth' && (
          <div className="space-y-5">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl mb-3">
                <Lock className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Enter your password</h2>
              <p className="text-sm text-gray-600 mt-2 flex items-center justify-center gap-2">
                <Mail className="w-4 h-4" />
                {entryIdentifier}
              </p>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              setLoading(true);
              setMessage('');
              
              try {
                const isPhoneNumber = isPhone(entryIdentifier);
                let res;
                
                if (isPhoneNumber) {
                  // Use phone + password login
                  const normalizedPhone = normalizePhoneNumber(entryIdentifier);
                  res = await AuthService.loginWithPhone(normalizedPhone, signinData.password, null);
                } else {
                  // Use email + password login - normalize using emailUtils
                  const loginEmail = normalizeEmail(entryIdentifier);
                  res = await AuthService.login(loginEmail, signinData.password, null);
                }
                if (res.success) {
                  // Reset wrong password count on successful login
                  setWrongPasswordCount(0);
                  await ensureUserProfile(auth.currentUser);
                  clearLoginState();
                  navigate('/dashboard');
                } else {
                  // Use the actual error message from AuthService (enhanced error messages)
                  const errorMsg = res.error || 'Login failed. Please try again.';
                  console.log('Login error:', errorMsg);
                  
                  // Increment attempt counter
                  const newCount = wrongPasswordCount + 1;
                  setWrongPasswordCount(newCount);
                  
                  // Create error object for notification
                  const loginError = new Error(errorMsg);
                  loginError.code = res.errorCode || 'AUTH_ERROR';
                  
                  // Show notification based on error type
                  if (res.errorCode?.startsWith('auth/')) {
                    handleFirebaseError(loginError, {
                      title: newCount >= 3 ? 'Login Failed - Multiple Attempts' : 'Login Failed',
                      context: 'Authentication',
                      duration: newCount >= 3 ? 10000 : 7000
                    });
                  } else {
                    handleErrorNotification(loginError, {
                      title: newCount >= 3 ? 'Login Failed - Multiple Attempts' : 'Login Failed',
                      context: 'Authentication',
                      duration: newCount >= 3 ? 10000 : 7000
                    });
                  }
                  
                  // Show specific error message with attempt count if multiple failures
                  if (newCount >= 3) {
                    setMessage(`${errorMsg} (Attempt ${newCount})`);
                    setShowForgotPassword(true);
                  } else if (newCount > 1) {
                    setMessage(`${errorMsg} (Attempt ${newCount})`);
                  } else {
                    setMessage(errorMsg);
                  }
                }
              } catch (e) {
                console.log('Login exception:', e);
                const newCount = wrongPasswordCount + 1;
                setWrongPasswordCount(newCount);
                
                const errorMsg = e?.message || 'An unexpected error occurred. Please try again.';
                
                // Show notification
                if (e.code?.startsWith('auth/')) {
                  handleFirebaseError(e, {
                    title: newCount >= 3 ? 'Login Failed - Multiple Attempts' : 'Login Failed',
                    context: 'Authentication',
                    duration: newCount >= 3 ? 10000 : 7000
                  });
                } else {
                  handleErrorNotification(e, {
                    title: newCount >= 3 ? 'Login Failed - Multiple Attempts' : 'Login Failed',
                    context: 'Authentication',
                    duration: newCount >= 3 ? 10000 : 7000
                  });
                }
                
                if (newCount >= 3) {
                  setMessage(`${errorMsg} (Attempt ${newCount})`);
                  setShowForgotPassword(true);
                } else if (newCount > 1) {
                  setMessage(`${errorMsg} (Attempt ${newCount})`);
                } else {
                  setMessage(errorMsg);
                }
              } finally {
                setLoading(false);
              }
            }} className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Lock className="w-4 h-4 text-blue-600" />
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type={showSigninPassword ? "text" : "password"}
                    value={signinData.password}
                    onChange={(e) => setSigninData({...signinData, password: e.target.value})}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !loading && signinData.password.trim()) {
                        e.target.form.requestSubmit();
                      }
                    }}
                    placeholder="Enter your password"
                    className="w-full pl-12 pr-12 py-3.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowSigninPassword(!showSigninPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none p-1 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    {showSigninPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-3.5 px-4 rounded-xl hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-[1.02] flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    <span>Sign In</span>
                  </>
                )}
              </button>
            </form>

            <div className="text-center space-y-3">
              <button
                onClick={() => {
                  setCurrentStep('entry');
                  setWrongPasswordCount(0);
                  setMessage('');
                  setShowForgotPassword(false);
                }}
                className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to entry
              </button>
              
              {wrongPasswordCount >= 3 && (
                <button
                  onClick={() => {
                    console.log('Forgot password clicked, wrongPasswordCount:', wrongPasswordCount);
                    setShowForgotPassword(true);
                  }}
                  className="text-red-600 hover:text-red-700 text-sm block w-full"
                >
                  Forgot Password?
                </button>
              )}
              
              {/* Debug info - remove in production */}
              {process.env.NODE_ENV === 'development' && (
                <div className="text-xs text-gray-500 text-center">
                  Debug: Wrong attempts: {wrongPasswordCount}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 2: OTP Verification */}
        {currentStep === 'otp' && (
          <div className="space-y-5">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl mb-3">
                <Smartphone className="w-8 h-8 text-indigo-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Verify your phone</h2>
              <p className="text-sm text-gray-600 mt-2 flex items-center justify-center gap-2">
                <Phone className="w-4 h-4" />
                {phoneNumber}
              </p>
            </div>

            {/* Warning Message for OTP */}
            <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <svg className="h-5 w-5 text-yellow-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-yellow-800 font-medium">
                    <strong>Please don't refresh the page</strong> - Your OTP will be resent automatically if needed.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 text-center">Enter 6-digit code</label>
              <input
                type="text"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-center text-3xl tracking-[0.5em] font-semibold bg-gray-50 focus:bg-white transition-all"
                maxLength={6}
              />
            </div>

            <button
              onClick={verifyOTPAndContinue}
              disabled={loading || otpCode.length !== 6}
              className="w-full bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white py-3.5 px-4 rounded-xl hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-[1.02] flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Verifying...</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-5 h-5" />
                  <span>Verify & Continue</span>
                </>
              )}
            </button>

            <div className="text-center space-y-2">
              <button
                onClick={async () => {
                  try {
                    setLoading(true);
                    setMessage('');
                    await sendPhoneOTP();
                    setMessage('OTP sent successfully!');
                  } catch (error) {
                    setMessage('Failed to resend OTP. Please try again.');
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
                className="text-green-600 hover:text-green-700 text-sm block w-full"
              >
                {loading ? 'Sending...' : 'Resend OTP'}
              </button>
              
              <button
                onClick={() => setCurrentStep('phone')}
                className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to phone number
              </button>
            </div>
              </div>
            )}

        {/* Page 2: Signup (New User) */}
        {currentStep === 'signup' && (
          <div className="space-y-4">
            <div className="text-center">
              <h2 className="text-lg font-semibold text-gray-900">
                {auth.currentUser ? 'Complete your account' : 'Create your account'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {auth.currentUser 
                  ? `Welcome ${auth.currentUser.displayName || auth.currentUser.email}!`
                  : `Sign up with ${entryIdentifier}`
                }
              </p>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              setLoading(true);
              setMessage('');
              
              try {
                const isPhoneNumber = isPhone(entryIdentifier);
                
                // Pre-fill user details based on entry or Google user
                const preFilledDetails = {
                  name: auth.currentUser?.displayName || userDetails.name,
                  email: auth.currentUser?.email || (isPhoneNumber ? userDetails.email : entryIdentifier),
                  recoveryPhone: isPhoneNumber ? entryIdentifier : userDetails.recoveryPhone,
                  password: userDetails.password
                };
                
                setUserDetails(preFilledDetails);
                setCurrentStep('details');
              } catch (error) {
                setMessage('Error preparing signup. Please try again.');
              } finally {
                setLoading(false);
              }
            }} className="space-y-3">
              <input
                type="text"
                value={auth.currentUser?.displayName || userDetails.name}
                onChange={(e) => setUserDetails({...userDetails, name: e.target.value})}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !loading && (auth.currentUser?.displayName || userDetails.name.trim())) {
                    e.target.form.requestSubmit();
                  }
                }}
                placeholder="Full Name"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              
              {(isPhone(entryIdentifier) || auth.currentUser) && (
                <input
                  type="email"
                  value={auth.currentUser?.email || userDetails.email}
                  onChange={(e) => setUserDetails({...userDetails, email: e.target.value})}
                  placeholder="Email (Optional)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              )}
              
              {!isPhone(entryIdentifier) && (
                <input
                  type="tel"
                  value={userDetails.recoveryPhone}
                  onChange={(e) => setUserDetails({...userDetails, recoveryPhone: e.target.value})}
                  placeholder="Phone Number (Optional)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              )}
              
              <input
                type="password"
                value={userDetails.password}
                onChange={(e) => setUserDetails({...userDetails, password: e.target.value})}
                placeholder="Password"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-lg font-medium"
              >
                {loading ? 'Creating...' : 'Create Account'}
              </button>
            </form>

            <div className="text-center">
              <button
                onClick={() => setCurrentStep('entry')}
                className="text-blue-600 hover:text-blue-700 text-sm"
              >
                ← Back to entry
              </button>
            </div>
          </div>
        )}

        {/* Step 3: User Details Collection */}
        {currentStep === 'details' && (
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-2xl mb-2">👤</div>
              <h2 className="text-lg font-semibold text-gray-900">Complete your profile</h2>
              <p className="text-sm text-gray-600">Help us personalize your experience</p>
          </div>

            <div className="space-y-3">
            <input
              type="text"
                value={userDetails.name}
                onChange={(e) => setUserDetails({...userDetails, name: e.target.value})}
                placeholder="Full Name *"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />

            <input
              type="email"
                value={userDetails.email}
                onChange={(e) => setUserDetails({...userDetails, email: e.target.value})}
                placeholder="Email (Optional - for recovery)"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />

              <input
                type="tel"
                value={userDetails.recoveryPhone}
                onChange={(e) => setUserDetails({...userDetails, recoveryPhone: e.target.value})}
                placeholder="Recovery Phone (Optional)"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />

              {/* Password Field */}
              <div>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={userDetails.password}
                    onChange={(e) => {
                      const newPassword = e.target.value;
                      setUserDetails({...userDetails, password: newPassword});
                      // Validate password for UI feedback
                      try {
                        const isValid = validatePassword(newPassword);
                        // Show warning if password is entered but doesn't meet requirements
                        if (newPassword.length > 0 && !isValid && newPassword.length >= 4) {
                          // Only show warning if user has typed a reasonable amount
                          const missingRequirements = [];
                          const requirements = {
                            length: newPassword.length >= 8,
                            uppercase: /[A-Z]/.test(newPassword),
                            lowercase: /[a-z]/.test(newPassword),
                            number: /\d/.test(newPassword),
                            symbol: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword)
                          };
                          if (!requirements.length) missingRequirements.push('at least 8 characters');
                          if (!requirements.uppercase) missingRequirements.push('uppercase letter');
                          if (!requirements.lowercase) missingRequirements.push('lowercase letter');
                          if (!requirements.number) missingRequirements.push('number');
                          if (!requirements.symbol) missingRequirements.push('special character');
                          
                          // Only show warning notification once per password change, not on every keystroke
                          // This is handled by the inline requirements display, so we skip notification here
                          // to avoid spam. Notification will show on submit if password is invalid.
                        }
                      } catch (error) {
                        console.error('Password validation error:', error);
                      }
                    }}
                    placeholder="Create Password *"
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                
                {/* Password Requirements */}
                {userDetails.password && passwordRequirements && (
                  <div className="mt-2 text-xs space-y-1">
                    <div className={`flex items-center ${passwordRequirements.length ? 'text-green-600' : 'text-red-600'}`}>
                      <span className="mr-2">{passwordRequirements.length ? '✓' : '✗'}</span>
                      At least 8 characters
                    </div>
                    <div className={`flex items-center ${passwordRequirements.uppercase ? 'text-green-600' : 'text-red-600'}`}>
                      <span className="mr-2">{passwordRequirements.uppercase ? '✓' : '✗'}</span>
                      One uppercase letter (A-Z)
                    </div>
                    <div className={`flex items-center ${passwordRequirements.lowercase ? 'text-green-600' : 'text-red-600'}`}>
                      <span className="mr-2">{passwordRequirements.lowercase ? '✓' : '✗'}</span>
                      One lowercase letter (a-z)
                    </div>
                    <div className={`flex items-center ${passwordRequirements.number ? 'text-green-600' : 'text-red-600'}`}>
                      <span className="mr-2">{passwordRequirements.number ? '✓' : '✗'}</span>
                      One number (0-9)
                    </div>
                    <div className={`flex items-center ${passwordRequirements.symbol ? 'text-green-600' : 'text-red-600'}`}>
                      <span className="mr-2">{passwordRequirements.symbol ? '✓' : '✗'}</span>
                      One symbol (!@#$%^&*)
                    </div>
                  </div>
                )}
              </div>

            </div>

            {/* Warning Message */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-800">
                    <strong>Please don't refresh the page</strong> - Your progress will be saved automatically.
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={completeSignup}
              disabled={loading || !userDetails.name || !userDetails.password || !(userDetails.password && userDetails.password.length >= 8)}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-lg font-medium"
            >
              {loading ? 'Creating account...' : 'Complete Setup'}
            </button>

            <button
              onClick={() => setCurrentStep('otp')}
              className="w-full text-gray-600 hover:text-gray-800 text-sm"
            >
              ← Back to verification
            </button>
          </div>
        )}

        {/* Alternative Sign In */}
        {currentStep === 'signin' && (
          <div className="space-y-4">
            <div className="text-center">
              <h2 className="text-lg font-semibold text-gray-900">Sign in to your account</h2>
            </div>

            <form onSubmit={doAlternativeSignin} className="space-y-3">
            <input
              type="text"
                value={signinData.identifier}
                onChange={(e) => setSigninData({...signinData, identifier: e.target.value})}
                placeholder="Email or Phone Number"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              <div>
            <input
              type="password"
                  value={signinData.password}
                  onChange={(e) => {
                    const newPassword = e.target.value;
                    setSigninData({...signinData, password: newPassword});
                    // Validate password for UI feedback
                    try {
                      validatePassword(newPassword);
                    } catch (error) {
                      console.error('Password validation error:', error);
                    }
                  }}
              placeholder="Password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
                
                {/* Password Requirements */}
                {signinData.password && passwordRequirements && (
                  <div className="mt-2 text-xs space-y-1">
                    <div className={`flex items-center ${passwordRequirements.length ? 'text-green-600' : 'text-red-600'}`}>
                      <span className="mr-2">{passwordRequirements.length ? '✓' : '✗'}</span>
                      At least 8 characters
                    </div>
                    <div className={`flex items-center ${passwordRequirements.uppercase ? 'text-green-600' : 'text-red-600'}`}>
                      <span className="mr-2">{passwordRequirements.uppercase ? '✓' : '✗'}</span>
                      One uppercase letter (A-Z)
                    </div>
                    <div className={`flex items-center ${passwordRequirements.lowercase ? 'text-green-600' : 'text-red-600'}`}>
                      <span className="mr-2">{passwordRequirements.lowercase ? '✓' : '✗'}</span>
                      One lowercase letter (a-z)
                    </div>
                    <div className={`flex items-center ${passwordRequirements.number ? 'text-green-600' : 'text-red-600'}`}>
                      <span className="mr-2">{passwordRequirements.number ? '✓' : '✗'}</span>
                      One number (0-9)
                    </div>
                    <div className={`flex items-center ${passwordRequirements.symbol ? 'text-green-600' : 'text-red-600'}`}>
                      <span className="mr-2">{passwordRequirements.symbol ? '✓' : '✗'}</span>
                      One symbol (!@#$%^&*)
                    </div>
                  </div>
                )}
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-lg font-medium"
              >
                {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

            <div className="text-center">
              <button
                onClick={() => setCurrentStep('phone')}
                className="text-blue-600 hover:text-blue-700 text-sm"
              >
                ← Back to main flow
              </button>
            </div>
          </div>
        )}

        {/* Biometric Login for Mobile (when available) - OPTIONAL */}
        {showBiometric && currentStep === 'phone' && !skipBiometric && (
              <div className="mt-4">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">Or</span>
                  </div>
                </div>
                {/* Alternative Login Options */}
                <div className="mt-4 space-y-2">
                  <button
                    onClick={() => setCurrentStep('signin')}
                    className="w-full bg-gray-100 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-200 text-sm font-medium"
                  >
                    📧 Use Email/Password instead
                  </button>
                </div>
              </div>
            )}

        {/* Show alternative login options when biometric is skipped */}
        {showBiometric && skipBiometric && currentStep === 'phone' && (
          <div className="mt-4">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or</span>
              </div>
            </div>
            <div className="mt-4 space-y-3">
              <button
                onClick={() => setCurrentStep('signin')}
                className="w-full bg-gray-100 text-gray-800 py-3 px-4 rounded-lg hover:bg-gray-200 text-lg font-medium"
              >
                📧 Sign in with Email/Password
              </button>
            </div>
          </div>
        )}

        {/* Messages */}
        {message && (
          <div className={`mt-4 text-sm rounded-lg p-3 text-center ${
            message.toLowerCase().includes('fail') || message.toLowerCase().includes('error') || message.toLowerCase().includes('invalid') || message.includes('⚠️')
              ? 'bg-red-50 text-red-700 border border-red-200'
              : 'bg-green-50 text-green-700 border border-green-200'
          }`}>
            {message}
        </div>
        )}

        {/* Password Requirements Info */}
        {currentStep === 'signin' && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Password Requirements:</h4>
            <ul className="text-xs text-blue-800 space-y-1">
              <li>• <strong>Length:</strong> At least 8 characters</li>
              <li>• <strong>Uppercase:</strong> One letter (A-Z)</li>
              <li>• <strong>Lowercase:</strong> One letter (a-z)</li>
              <li>• <strong>Number:</strong> One digit (0-9)</li>
              <li>• <strong>Symbol:</strong> One special character (!@#$%^&*)</li>
            </ul>
          </div>
        )}

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Reset Password</h3>
              <p className="text-sm text-gray-600 mb-4">
                {isPhone(entryIdentifier) 
                  ? `We'll send an OTP to ${entryIdentifier} to reset your password.`
                  : `We'll send a password reset link to ${entryIdentifier}.`
                }
              </p>
              
              <div className="space-y-4">
                <input
                  type={isPhone(entryIdentifier) ? "tel" : "email"}
                  value={entryIdentifier}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                />
                
                <div className="flex space-x-3">
                  <button
                    onClick={async () => {
                      try {
                        setLoading(true);
                        
                        if (isPhone(entryIdentifier)) {
                          // Phone: Send OTP for password reset
                          const normalizedPhone = normalizePhoneNumber(entryIdentifier);
                          setPhoneNumber(normalizedPhone);
                          setCurrentStep('otp');
                          await sendPhoneOTP(); // Actually send the OTP
                          setMessage('OTP sent to your phone for password reset.');
                          setShowForgotPassword(false);
                          setWrongPasswordCount(0);
                        } else {
                          // Email: Send reset link
                          const email = entryIdentifier.trim().toLowerCase();
                          const res = await AuthService.resetPassword(email);
                          if (res.success) {
                            setMessage('Password reset link sent to your email.');
                            setShowForgotPassword(false);
                          } else {
                            setMessage(res.error || 'Failed to send reset link');
                          }
                        }
                      } catch (error) {
                        setMessage(error?.message || 'Failed to send reset link');
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Sending...' : isPhone(entryIdentifier) ? 'Send OTP' : 'Send Reset Link'}
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowForgotPassword(false);
                      setWrongPasswordCount(0);
                      setMessage('');
                    }}
                    className="flex-1 bg-gray-100 text-gray-900 py-2 px-4 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recaptcha container */}
        <div id="login-recaptcha-container"></div>
        </div>
      </div>
    </div>
  );
}



